import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getProfileSections,
  PQSection,
} from 'src/api/dataVault/profileSections';

type Status = 'idle' | 'loading' | 'succeeded' | 'failed';

interface Entry {
  status: Status;
  sections: PQSection[];
  error: string | null;
  lastFetchedAt: number | null;
}

interface State {
  byTargetUserId: Record<number, Entry>;
}

const STALE_MS = 30_000;

const initialState: State = { byTargetUserId: {} };

export const fetchProfileSections = createAsyncThunk(
  'dataVaultSections/fetch',
  async (
    {
      targetUserId,
      actingOnMemberId,
      forceRefresh = false,
    }: {
      targetUserId: number;
      actingOnMemberId?: number | null;
      forceRefresh?: boolean;
    },
    { getState, rejectWithValue }
  ) => {
    const state = (getState() as { dataVaultSections: State })
      .dataVaultSections;
    const entry = state.byTargetUserId[targetUserId];
    if (
      !forceRefresh &&
      entry?.status === 'succeeded' &&
      entry.lastFetchedAt &&
      Date.now() - entry.lastFetchedAt < STALE_MS
    ) {
      return null; // cached
    }
    try {
      return await getProfileSections({ actingOnMemberId });
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      return rejectWithValue(
        err?.response?.data?.detail ?? 'Failed to load profile sections'
      );
    }
  }
);

const slice = createSlice({
  name: 'dataVaultSections',
  initialState,
  reducers: {
    invalidateSubject(state, action: PayloadAction<number>) {
      delete state.byTargetUserId[action.payload];
    },
    clearEntity(state, action: PayloadAction<number>) {
      delete state.byTargetUserId[action.payload];
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchProfileSections.pending, (state, action) => {
        const id = action.meta.arg.targetUserId;
        const forceRefresh = action.meta.arg.forceRefresh ?? false;
        const entry = state.byTargetUserId[id];
        // If data is still fresh and this isn't a forced refresh, the thunk body
        // will return null (cache hit) — don't clobber the existing succeeded entry.
        if (
          !forceRefresh &&
          entry?.status === 'succeeded' &&
          entry.lastFetchedAt &&
          Date.now() - entry.lastFetchedAt < STALE_MS
        ) {
          return;
        }
        state.byTargetUserId[id] = {
          status: 'loading',
          sections: [],
          error: null,
          lastFetchedAt: null,
        };
      })
      .addCase(fetchProfileSections.fulfilled, (state, action) => {
        const id = action.meta.arg.targetUserId;
        if (action.payload === null) {
          return;
        } // cached
        state.byTargetUserId[id] = {
          status: 'succeeded',
          sections: action.payload,
          error: null,
          lastFetchedAt: Date.now(),
        };
      })
      .addCase(fetchProfileSections.rejected, (state, action) => {
        const id = action.meta.arg.targetUserId;
        state.byTargetUserId[id] = {
          status: 'failed',
          sections: [],
          error: action.payload as string,
          lastFetchedAt: null,
        };
      });
  },
});

export const { invalidateSubject, clearEntity } = slice.actions;

export const selectSections =
  (targetUserId: number) =>
  (state: { dataVaultSections: State }): Entry | undefined =>
    state.dataVaultSections.byTargetUserId[targetUserId];

export default slice.reducer;
